import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Skeleton } from './ui/skeleton';
import { Switch } from './ui/switch';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { 
  MapPin, Loader2, Plus, Filter, X, Check, ChevronDown, ChevronUp,
  ChevronsUpDown, Star, Hotel as HotelIcon, AlertCircle, Calendar,
  ArrowUpDown, Trash2, Settings, Percent, IndianRupee, DollarSign,
  Users, Bed, Car, Save, CreditCard, Building2, Search, Copy, Layers
} from 'lucide-react';
import api from '../utils/api';
import { QuickAddHotelModal } from './quick-add';
import { formatCurrency } from '../utils/helpers';

// Option name suggestions
const OPTION_NAME_SUGGESTIONS = ['Budget', 'Standard', 'Deluxe', 'Premium', 'Luxury', 'Economy'];
const MAX_OPTIONS = 4;

const MEAL_PLANS = [
  { value: 'EP', label: 'Room Only (EP)' },
  { value: 'CP', label: 'Breakfast (CP)' },
  { value: 'MAP', label: 'B\'fast + Dinner (MAP)' },
  { value: 'AP', label: 'All Meals (AP)' }
];

const SORT_OPTIONS = [
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'star_desc', label: 'Star Rating: High to Low' },
  { value: 'star_asc', label: 'Star Rating: Low to High' },
  { value: 'name_asc', label: 'Name: A to Z' }
];

const GST_RATES = ['0', '5', '12', '18'];
const GST_APPLY_ON = [
  { value: 'total', label: 'Total Package Amount' },
  { value: 'markup', label: 'Markup Amount Only' },
];

const COST_TYPES = [
  { value: 'per_person', label: 'Per Person' },
  { value: 'per_room', label: 'Per Room' },
  { value: 'per_night', label: 'Per Night' },
  { value: 'total', label: 'Total (Fixed)' },
];

/**
 * CustomPackageHotelSelector - Step 3 of Custom Package Flow
 * 
 * Features:
 * - Accordion layout with check-in/check-out dates per stop
 * - 4 cascading filters (Star Rating, Category Band, Room Type, Meal Plan)
 * - Sort functionality (Price, Star Rating, Name)
 * - Date-aware pricing from backend
 * - Radio selection for hotels with split stay support
 * - Upsell section for room and meal upgrades
 * - Markup, Discount, GST configuration
 */
const CustomPackageHotelSelector = ({ 
  customPackageData,
  onSelectionsChange,
  hotelSelections = {},
  formData = {},
  handleInputChange,
  vehicleOptions = [],
  places = [], // For Quick-Add Hotel modal
}) => {
  // Accordion state
  const [expandedStops, setExpandedStops] = useState(['stop_0']);
  
  // Loading states
  const [fetchingHotels, setFetchingHotels] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Masters data
  const [categoryBands, setCategoryBands] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [agencyMasters, setAgencyMasters] = useState([]);
  
  // Hotels cache per place (with date-aware rates)
  const [hotelsCache, setHotelsCache] = useState({});
  
  // Filters per stop
  const [filters, setFilters] = useState({});
  
  // Hotel name search per stop
  const [hotelSearch, setHotelSearch] = useState({});
  
  // Sort per stop
  const [sortBy, setSortBy] = useState({});
  
  // Show all toggle per stop
  const [showAllHotels, setShowAllHotels] = useState({});
  
  // Upgrade visibility per hotel
  const [showUpgrades, setShowUpgrades] = useState({});
  
  // Rate breakdown visibility per hotel (NEW: for expandable rate display)
  const [showRateBreakdown, setShowRateBreakdown] = useState({});
  
  // Vehicle uplift selections for Quote Preview
  const [selectedVehicleUplifts, setSelectedVehicleUplifts] = useState({});
  
  // Quote Preview visibility - show when hotels are selected for all stops
  const [showQuotePreview, setShowQuotePreview] = useState(false);
  
  // Payment policies
  const [paymentPolicies, setPaymentPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(formData.payment_policy_id || null);
  
  // Quick-Add Hotel Modal state
  const [quickAddHotelOpen, setQuickAddHotelOpen] = useState(false);
  const [quickAddHotelPlaceId, setQuickAddHotelPlaceId] = useState('');
  const [quickAddHotelStopIndex, setQuickAddHotelStopIndex] = useState(null);

  // ============ MULTI-OPTION STATE ============
  // Active option index for editing
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  
  // Initialize options from formData or create default
  const getInitialOptions = useCallback(() => {
    if (formData.custom_package_options?.length > 0) {
      return formData.custom_package_options;
    }
    // Create default first option
    return [{
      id: Date.now(),
      option_name: 'Option 1',
      selected_hotels: {},
      collapsed: false,
    }];
  }, [formData.custom_package_options]);

  // Sync options to formData
  const updateOptions = useCallback((newOptions) => {
    handleInputChange?.('custom_package_options', newOptions);
    
    // Also sync first option's hotels to hotelSelections for backward compatibility
    if (newOptions.length > 0 && onSelectionsChange) {
      onSelectionsChange(newOptions[0].selected_hotels || {});
    }
  }, [handleInputChange, onSelectionsChange]);

  // Get current options (from formData or default)
  const options = useMemo(() => {
    return formData.custom_package_options?.length > 0 
      ? formData.custom_package_options 
      : getInitialOptions();
  }, [formData.custom_package_options, getInitialOptions]);

  // Add new option
  const addOption = useCallback(() => {
    if (options.length >= MAX_OPTIONS) return;
    
    const newOption = {
      id: Date.now(),
      option_name: `Option ${options.length + 1}`,
      selected_hotels: {},
      collapsed: false,
    };
    
    const newOptions = [...options, newOption];
    updateOptions(newOptions);
    setActiveOptionIndex(newOptions.length - 1);
  }, [options, updateOptions]);

  // Copy existing option
  const copyOption = useCallback((index) => {
    if (options.length >= MAX_OPTIONS) return;
    
    const sourceOption = options[index];
    const newOption = {
      ...JSON.parse(JSON.stringify(sourceOption)), // Deep copy
      id: Date.now(),
      option_name: `${sourceOption.option_name} (Copy)`,
    };
    
    const newOptions = [...options, newOption];
    updateOptions(newOptions);
    setActiveOptionIndex(newOptions.length - 1);
  }, [options, updateOptions]);

  // Remove option
  const removeOption = useCallback((index) => {
    if (options.length <= 1) return; // Keep at least one option
    
    const newOptions = options.filter((_, i) => i !== index);
    updateOptions(newOptions);
    
    // Adjust active index if needed
    if (activeOptionIndex >= newOptions.length) {
      setActiveOptionIndex(newOptions.length - 1);
    } else if (activeOptionIndex > index) {
      setActiveOptionIndex(activeOptionIndex - 1);
    }
  }, [options, activeOptionIndex, updateOptions]);

  // Update option field
  const updateOptionField = useCallback((index, field, value) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    updateOptions(newOptions);
  }, [options, updateOptions]);

  // Update option's selected hotel for a place
  const updateOptionHotel = useCallback((optionIndex, placeName, hotelData) => {
    const newOptions = [...options];
    const currentHotels = newOptions[optionIndex].selected_hotels || {};
    newOptions[optionIndex] = {
      ...newOptions[optionIndex],
      selected_hotels: {
        ...currentHotels,
        [placeName]: hotelData,
      },
    };
    updateOptions(newOptions);
  }, [options, updateOptions]);

  // Toggle option collapse
  const toggleOptionCollapse = useCallback((index) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], collapsed: !newOptions[index].collapsed };
    updateOptions(newOptions);
  }, [options, updateOptions]);

  // ============ END MULTI-OPTION STATE ============

  // Memoize stops and arrival date
  const stops = useMemo(() => customPackageData?.stops || [], [customPackageData?.stops]);
  const arrivalDate = useMemo(() => customPackageData?.arrival_date, [customPackageData?.arrival_date]);
  
  // Get explore_alternative_rooms flag from room_config
  const exploreAlternativeRooms = useMemo(() => 
    formData?.room_config?.explore_alternative_rooms || false, 
    [formData?.room_config?.explore_alternative_rooms]
  );
  
  // Calculate total passengers needing beds for alternative room filtering
  const totalPaxNeedingBeds = useMemo(() => {
    const roomConfig = formData?.room_config || {};
    return (roomConfig.single_rooms || 0) + 
      ((roomConfig.double_rooms || 0) * 2) + 
      ((roomConfig.triple_rooms || 0) * 3) + 
      (roomConfig.adult_extra_beds || roomConfig.extra_beds || 0) +
      (roomConfig.children_with_bed || 0);
  }, [formData?.room_config]);

  // Calculate check-in/check-out dates for each stop
  const stopsWithDates = useMemo(() => {
    if (!arrivalDate || stops.length === 0) return [];
    
    let currentDate = new Date(arrivalDate);
    
    return stops.map((stop, index) => {
      const checkIn = new Date(currentDate);
      const nights = stop.nights || 1;
      const checkOut = new Date(currentDate);
      checkOut.setDate(checkOut.getDate() + nights);
      
      // Calculate night details
      const nightDetails = [];
      let tempDate = new Date(checkIn);
      for (let i = 0; i < nights; i++) {
        const dayOfWeek = tempDate.getDay();
        // Default weekend: Friday (5), Saturday (6)
        const isWeekend = (dayOfWeek === 5 || dayOfWeek === 6);
        nightDetails.push({
          date: tempDate.toISOString().split('T')[0],
          dayType: isWeekend ? 'weekend' : 'weekday',
          dayName: tempDate.toLocaleDateString('en-US', { weekday: 'short' })
        });
        tempDate.setDate(tempDate.getDate() + 1);
      }
      
      // Move to next stop
      currentDate = new Date(checkOut);
      
      return {
        ...stop,
        stopIndex: index,
        check_in: checkIn.toISOString().split('T')[0],
        check_out: checkOut.toISOString().split('T')[0],
        check_in_display: checkIn.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        check_out_display: checkOut.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        night_details: nightDetails,
        weekday_nights: nightDetails.filter(n => n.dayType === 'weekday').length,
        weekend_nights: nightDetails.filter(n => n.dayType === 'weekend').length
      };
    });
  }, [arrivalDate, stops]);

  // Auto-show Quote Preview when all stops have hotels allocated (for active option)
  useEffect(() => {
    const activeOption = options[activeOptionIndex];
    const selectedHotels = activeOption?.selected_hotels || {};
    
    if (!selectedHotels || Object.keys(selectedHotels).length === 0) {
      setShowQuotePreview(false);
      return;
    }
    
    // Check if all stops have at least one hotel selected
    const allStopsHaveHotels = stopsWithDates.every((stop, index) => {
      const key = `stop_${index}`;
      const selection = selectedHotels[key];
      return selection && selection.hotels && selection.hotels.length > 0;
    });
    
    setShowQuotePreview(allStopsHaveHotels && stopsWithDates.length > 0);
  }, [options, activeOptionIndex, stopsWithDates]);

  // Fetch masters data on mount
  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [bandsResponse, mastersResponse, agencyMastersResponse, policiesResponse] = await Promise.all([
          api.get('/admin/category-bands'),
          api.get('/hotel-rates/masters'),
          api.get('/admin/masters'),
          api.get('/payment-policies')
        ]);
        setCategoryBands(bandsResponse.data || []);
        setRoomTypes(mastersResponse.data?.roomTypes || []);
        setAgencyMasters(agencyMastersResponse.data || []);
        setPaymentPolicies(policiesResponse.data || []);
        
        // Set default policy if not already set
        if (!formData?.payment_policy_id && typeof handleInputChange === 'function') {
          const defaultPolicy = policiesResponse.data?.find(p => p.is_default);
          if (defaultPolicy) {
            setSelectedPolicy(defaultPolicy.policy_id);
            try {
              handleInputChange('payment_policy_id', defaultPolicy.policy_id);
            } catch (e) {
              console.error('Failed to set default policy:', e);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch masters:', error);
      }
    };
    
    fetchMasters();
  }, []);

  // Auto-sync transfer_days when auto-calculate is on or when stops change
  useEffect(() => {
    // Early exit if data isn't ready
    if (!customPackageData || typeof handleInputChange !== 'function') return;
    
    // Calculate auto transfer days
    const totalNights = Array.isArray(stopsWithDates) 
      ? stopsWithDates.reduce((sum, s) => sum + (s.nights || 0), 0)
      : 0;
    const autoTransferDays = totalNights + 1;
    
    // Only update if auto-calculate is enabled (default = true)
    const isAutoCalc = customPackageData.transfer_days_auto !== false;
    const currentTransferDays = customPackageData.transfer_days || 0;
    
    // Only update if there's actually a difference and we have valid data
    if (isAutoCalc && currentTransferDays !== autoTransferDays && autoTransferDays > 1) {
      try {
        handleInputChange('custom_package_data', {
          ...customPackageData,
          transfer_days: autoTransferDays
        });
      } catch (e) {
        console.error('Failed to update transfer_days:', e);
      }
    }
  }, [stopsWithDates.length, customPackageData?.transfer_days_auto, customPackageData?.transfer_days]);

  // Fetch hotels with date-aware rates for all places
  useEffect(() => {
    const fetchAllHotels = async () => {
      if (fetchingHotels || stopsWithDates.length === 0) return;
      
      // Check if all hotels are already cached
      const allCached = stopsWithDates.every(stop => 
        hotelsCache[`${stop.place_id}_${stop.check_in}_${stop.check_out}`] !== undefined
      );
      
      if (allCached) {
        setInitialLoading(false);
        return;
      }

      setFetchingHotels(true);
      setInitialLoading(true);
      
      try {
        const results = [];
        
        for (const stop of stopsWithDates) {
          const cacheKey = `${stop.place_id}_${stop.check_in}_${stop.check_out}`;
          
          if (hotelsCache[cacheKey]) {
            results.push({ cacheKey, hotels: hotelsCache[cacheKey] });
            continue;
          }
          
          if (!stop.place_id) continue;
          
          try {
            const res = await api.get(`/hotels/by-place/${stop.place_id}/with-rates`, {
              params: {
                check_in: stop.check_in,
                check_out: stop.check_out
              }
            });
            const hotels = res.data?.hotels || [];
            results.push({ cacheKey, hotels });
          } catch (e) {
            console.error(`Failed to fetch hotels for ${stop.place_name}:`, e);
            // Fallback to non-date-aware API
            try {
              const fallbackRes = await api.get(`/hotels/by-place/${stop.place_id}`);
              results.push({ cacheKey, hotels: fallbackRes.data?.hotels || [] });
            } catch {
              results.push({ cacheKey, hotels: [] });
            }
          }
        }
        
        setHotelsCache(prev => {
          const newCache = { ...prev };
          results.forEach(({ cacheKey, hotels }) => {
            newCache[cacheKey] = hotels;
          });
          return newCache;
        });
      } catch (error) {
        console.error('Failed to fetch hotels:', error);
      } finally {
        setFetchingHotels(false);
        setInitialLoading(false);
      }
    };
    
    if (stopsWithDates.length > 0) {
      fetchAllHotels();
    } else {
      setInitialLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsWithDates.map(s => `${s.place_id}_${s.check_in}_${s.check_out}`).join(',')]);

  // Initialize selections for each stop
  useEffect(() => {
    const initSelections = { ...hotelSelections };
    let hasChanges = false;

    stopsWithDates.forEach((stop, index) => {
      const key = `stop_${index}`;
      
      if (!initSelections[key]) {
        initSelections[key] = {
          place_id: stop.place_id,
          place_name: stop.place_name,
          max_nights: stop.nights,
          check_in: stop.check_in,
          check_out: stop.check_out,
          hotels: []
        };
        hasChanges = true;
      } else {
        // Update dates if changed
        if (initSelections[key].check_in !== stop.check_in || 
            initSelections[key].check_out !== stop.check_out) {
          initSelections[key] = {
            ...initSelections[key],
            check_in: stop.check_in,
            check_out: stop.check_out
          };
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      onSelectionsChange(initSelections);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsWithDates]);

  // Get hotels for a stop (with cache key)
  const getHotelsForStop = useCallback((stop) => {
    const cacheKey = `${stop.place_id}_${stop.check_in}_${stop.check_out}`;
    return hotelsCache[cacheKey] || [];
  }, [hotelsCache]);

  // Get available star ratings from hotels
  const getAvailableStarRatings = useCallback((hotels) => {
    const ratings = [...new Set(hotels.map(h => h.star_rating).filter(Boolean))].sort((a, b) => a - b);
    return ratings;
  }, []);

  // Get available category bands based on filters (from rooms array)
  const getAvailableCategoryBands = useCallback((hotels, starFilter) => {
    let filteredHotels = hotels;
    
    if (starFilter && starFilter !== 'all') {
      filteredHotels = hotels.filter(h => h.star_rating === parseInt(starFilter));
    }
    
    const availableCategories = new Set();
    filteredHotels.forEach(hotel => {
      // Use the new rooms array structure
      const rooms = hotel.rooms || [];
      rooms.forEach(room => {
        if (room.room_category) {
          availableCategories.add(room.room_category);
        }
      });
    });
    
    return categoryBands.filter(band => 
      availableCategories.has(band.category_name)
    );
  }, [categoryBands]);

  // Get available room types based on filters (from rooms array) - Returns Room Types, not Room Names
  const getAvailableRoomTypes = useCallback((hotels, starFilter, categoryFilter) => {
    let filteredHotels = hotels;
    
    if (starFilter && starFilter !== 'all') {
      filteredHotels = filteredHotels.filter(h => h.star_rating === parseInt(starFilter));
    }
    
    const roomTypeSet = new Set();
    filteredHotels.forEach(hotel => {
      const rooms = hotel.rooms || [];
      rooms.forEach(room => {
        // If category filter active, only include room types from rooms in that category
        if (categoryFilter && categoryFilter !== 'all') {
          if (room.room_category === categoryFilter && room.room_type) {
            roomTypeSet.add(room.room_type);
          }
        } else if (room.room_type) {
          roomTypeSet.add(room.room_type);
        }
      });
    });
    
    return Array.from(roomTypeSet).sort();
  }, []);

  // Get available meal plans based on filters (from rooms array)
  const getAvailableMealPlans = useCallback((hotels, starFilter, categoryFilter, roomTypeFilter) => {
    let filteredHotels = hotels;
    
    if (starFilter && starFilter !== 'all') {
      filteredHotels = filteredHotels.filter(h => h.star_rating === parseInt(starFilter));
    }
    
    const availablePlans = new Set();
    filteredHotels.forEach(hotel => {
      const rooms = hotel.rooms || [];
      rooms.forEach(room => {
        // Check category filter
        if (categoryFilter && categoryFilter !== 'all') {
          if (room.room_category !== categoryFilter) return;
        }
        // Check room type filter - now filters by room_type, not room_name
        if (roomTypeFilter && roomTypeFilter !== 'all') {
          if (room.room_type !== roomTypeFilter) return;
        }
        // Add all meal plans from matching rooms
        Object.keys(room.rates || {}).forEach(mp => availablePlans.add(mp));
      });
    });
    
    return MEAL_PLANS.filter(mp => availablePlans.has(mp.value));
  }, []);

  // Helper: Get filtered rooms for a hotel based on current filters
  const getFilteredRooms = useCallback((hotel, stopFilters) => {
    const rooms = hotel.rooms || [];
    
    return rooms.filter(room => {
      // Category band filter - only show rooms in the selected category
      if (stopFilters.categoryBand && stopFilters.categoryBand !== 'all') {
        if (room.room_category !== stopFilters.categoryBand) return false;
      }
      
      // Room type filter - filters by room_type (Room, Suite, Villa), not room_name
      if (stopFilters.roomType && stopFilters.roomType !== 'all') {
        if (room.room_type !== stopFilters.roomType) return false;
      }
      
      // Meal plan filter - only show rooms that have that meal plan
      if (stopFilters.mealPlan && stopFilters.mealPlan !== 'all') {
        if (!room.rates || !room.rates[stopFilters.mealPlan]) return false;
      }
      
      // If exploring alternative rooms, also check max_occupancy
      // Show rooms that can fit the entire party OR standard rooms
      if (exploreAlternativeRooms && totalPaxNeedingBeds > 3) {
        // Show rooms with max_occupancy >= total pax OR standard rooms (max_occ <= 3)
        const maxOcc = room.max_occupancy || 3;
        // Keep standard rooms (they'll use the standard config)
        // Also show larger rooms that can fit the group
        if (maxOcc > 3 && maxOcc < totalPaxNeedingBeds) {
          // Room is larger than standard but too small for the group
          // Still show it as it could be combined
        }
      }
      
      return true;
    });
  }, [exploreAlternativeRooms, totalPaxNeedingBeds]);

  // Helper: Get the best room for display (based on filters)
  const getBestRoomForDisplay = useCallback((hotel, stopFilters) => {
    const filteredRooms = getFilteredRooms(hotel, stopFilters);
    if (filteredRooms.length === 0) return null;
    
    // Get the meal plan to use for rate comparison
    const mealPlanFilter = stopFilters.mealPlan && stopFilters.mealPlan !== 'all' 
      ? stopFilters.mealPlan 
      : 'CP';
    
    // Sort by calculated rate for the selected meal plan
    const sorted = [...filteredRooms].sort((a, b) => {
      const rateA = a.rates?.[mealPlanFilter]?.calculated || a.rates?.['CP']?.calculated || Infinity;
      const rateB = b.rates?.[mealPlanFilter]?.calculated || b.rates?.['CP']?.calculated || Infinity;
      return rateA - rateB;
    });
    
    return sorted[0];
  }, [getFilteredRooms]);

  // Helper: Get rate for a specific room and meal plan
  const getRoomRate = useCallback((room, mealPlan) => {
    if (!room || !room.rates) return 0;
    const rates = room.rates[mealPlan] || room.rates['CP'] || room.rates['MAP'] || Object.values(room.rates)[0];
    return rates?.calculated || 0;
  }, []);

  // Filter hotels based on all filters
  const getFilteredHotels = useCallback((hotels, stopIndex) => {
    const stopFilters = filters[stopIndex] || {};
    const searchTerm = (hotelSearch[stopIndex] || '').toLowerCase().trim();
    
    let filtered = hotels.filter(hotel => {
      // Hotel name search filter (case-insensitive, partial match)
      if (searchTerm) {
        const hotelName = (hotel.hotel_name || '').toLowerCase();
        if (!hotelName.includes(searchTerm)) return false;
      }
      
      // Star rating filter
      if (stopFilters.starRating && stopFilters.starRating !== 'all') {
        if (hotel.star_rating !== parseInt(stopFilters.starRating)) return false;
      }
      
      // Check if hotel has any rooms matching the filters
      const matchingRooms = getFilteredRooms(hotel, stopFilters);
      if (matchingRooms.length === 0) return false;
      
      return true;
    });
    
    // Get the meal plan for sorting
    const mealPlanFilter = stopFilters.mealPlan && stopFilters.mealPlan !== 'all' 
      ? stopFilters.mealPlan 
      : 'CP';
    
    // Apply sorting
    const sort = sortBy[stopIndex] || 'price_asc';
    filtered.sort((a, b) => {
      switch (sort) {
        case 'price_asc': {
          const roomA = getBestRoomForDisplay(a, stopFilters);
          const roomB = getBestRoomForDisplay(b, stopFilters);
          const rateA = getRoomRate(roomA, mealPlanFilter);
          const rateB = getRoomRate(roomB, mealPlanFilter);
          return rateA - rateB;
        }
        case 'price_desc': {
          const roomA = getBestRoomForDisplay(a, stopFilters);
          const roomB = getBestRoomForDisplay(b, stopFilters);
          const rateA = getRoomRate(roomA, mealPlanFilter);
          const rateB = getRoomRate(roomB, mealPlanFilter);
          return rateB - rateA;
        }
        case 'star_desc':
          return (b.star_rating || 0) - (a.star_rating || 0);
        case 'star_asc':
          return (a.star_rating || 0) - (b.star_rating || 0);
        case 'name_asc':
          return (a.hotel_name || '').localeCompare(b.hotel_name || '');
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [filters, hotelSearch, sortBy, getFilteredRooms, getBestRoomForDisplay, getRoomRate]);

  // Update filter (with cascading reset)
  const updateFilter = useCallback((stopIndex, filterField, value) => {
    setFilters(prev => {
      const currentFilters = prev[stopIndex] || {};
      const newFilters = { ...currentFilters, [filterField]: value };
      
      // Cascading reset
      if (filterField === 'starRating') {
        newFilters.categoryBand = 'all';
        newFilters.roomType = 'all';
        newFilters.mealPlan = 'all';
      } else if (filterField === 'categoryBand') {
        newFilters.roomType = 'all';
        newFilters.mealPlan = 'all';
      } else if (filterField === 'roomType') {
        newFilters.mealPlan = 'all';
      }
      
      return { ...prev, [stopIndex]: newFilters };
    });
    
    // Clear hotel selections for active option when filters change
    const key = `stop_${stopIndex}`;
    const newOptions = [...options];
    const currentHotels = newOptions[activeOptionIndex].selected_hotels || {};
    newOptions[activeOptionIndex] = {
      ...newOptions[activeOptionIndex],
      selected_hotels: {
        ...currentHotels,
        [key]: { ...currentHotels[key], hotels: [] }
      }
    };
    updateOptions(newOptions);
  }, [options, activeOptionIndex, updateOptions]);

  // Clear all filters
  const clearFilters = useCallback((stopIndex) => {
    setFilters(prev => ({
      ...prev,
      [stopIndex]: { starRating: 'all', categoryBand: 'all', roomType: 'all', mealPlan: 'all' }
    }));
    
    // Also clear search
    setHotelSearch(prev => ({
      ...prev,
      [stopIndex]: ''
    }));
    
    // Clear hotel selections for active option
    const key = `stop_${stopIndex}`;
    const newOptions = [...options];
    const currentHotels = newOptions[activeOptionIndex].selected_hotels || {};
    newOptions[activeOptionIndex] = {
      ...newOptions[activeOptionIndex],
      selected_hotels: {
        ...currentHotels,
        [key]: { ...currentHotels[key], hotels: [] }
      }
    };
    updateOptions(newOptions);
  }, [options, activeOptionIndex, updateOptions]);

  // Get used nights for a stop (uses active option's selections)
  const getUsedNights = useCallback((stopIndex) => {
    const key = `stop_${stopIndex}`;
    // Use active option's selected_hotels instead of hotelSelections prop
    const activeOption = options[activeOptionIndex];
    const selection = activeOption?.selected_hotels?.[key];
    if (!selection || !Array.isArray(selection.hotels)) return 0;
    return selection.hotels.reduce((sum, h) => sum + (parseInt(h.nights) || 0), 0);
  }, [options, activeOptionIndex]);

  // Get remaining nights
  const getRemainingNights = useCallback((stopIndex) => {
    const stop = stopsWithDates[stopIndex];
    const maxNights = stop?.nights || 1;
    return Math.max(0, maxNights - getUsedNights(stopIndex));
  }, [stopsWithDates, getUsedNights]);

  // Select a hotel (for active option)
  const selectHotel = useCallback((stopIndex, hotel, selectedRoom = null) => {
    const key = `stop_${stopIndex}`;
    const remainingNights = getRemainingNights(stopIndex);
    const stop = stopsWithDates[stopIndex];
    const stopFilters = filters[stopIndex] || {};
    
    // Use active option's selected_hotels
    const activeOption = options[activeOptionIndex];
    const currentSelection = activeOption?.selected_hotels?.[key] || { hotels: [] };
    const alreadySelected = currentSelection.hotels?.some(h => h.hotel_id === hotel.hotel_id);
    
    if (alreadySelected) {
      // Deselect - update via option state
      const newOptions = [...options];
      const currentHotels = newOptions[activeOptionIndex].selected_hotels || {};
      newOptions[activeOptionIndex] = {
        ...newOptions[activeOptionIndex],
        selected_hotels: {
          ...currentHotels,
          [key]: {
            ...currentHotels[key],
            hotels: (currentHotels[key]?.hotels || []).filter(h => h.hotel_id !== hotel.hotel_id)
          }
        }
      };
      updateOptions(newOptions);
      return;
    }
    
    if (remainingNights <= 0) return;
    
    // Get the best room based on filters (or use provided selectedRoom)
    const room = selectedRoom || getBestRoomForDisplay(hotel, stopFilters);
    if (!room) return; // No matching rooms
    
    // Determine meal plan (from filter or room's available plans)
    const availableMealPlans = Object.keys(room.rates || {});
    const mealPlan = (stopFilters.mealPlan && stopFilters.mealPlan !== 'all' && availableMealPlans.includes(stopFilters.mealPlan))
      ? stopFilters.mealPlan 
      : (availableMealPlans.includes('CP') ? 'CP' : availableMealPlans[0] || 'CP');
    
    // Get the rate for the selected meal plan
    const rateInfo = room.rates?.[mealPlan] || Object.values(room.rates || {})[0] || {};
    const rate = rateInfo.calculated || 0;
    
    const newHotelEntry = {
      id: Date.now(),
      hotel_id: hotel.hotel_id,
      hotel_name: hotel.hotel_name,
      star_rating: hotel.star_rating,
      // Room-specific info from the selected room
      room_name: room.room_name,
      room_type: room.room_type,
      room_category: room.room_category,
      max_occupancy: room.max_occupancy || 3,
      base_occupancy: room.base_occupancy || 2,
      // Rate info
      lowest_rate: rate,
      room_gst_slab: room.room_gst_slab ?? null,
      rate_extra_adult: room.rate_extra_adult || 0,
      rate_child_bed: room.rate_child_bed || 0,
      // Rates object for dynamic recalculation
      rates: room.rates || {},
      // Selection
      meal_plan: mealPlan,
      nights: remainingNights,
      available_meal_plans: availableMealPlans,
      // All rooms for this hotel (for upgrades/alternatives)
      all_rooms: hotel.rooms || [],
      // Stay info
      rate_breakdown: hotel.rate_breakdown,
      check_in: stop.check_in,
      check_out: stop.check_out,
      weekday_nights: hotel.weekday_nights,
      weekend_nights: hotel.weekend_nights,
      upgrades: {
        room: [],
        meal: []
      }
    };
    
    // Add hotel to active option's selected_hotels
    const newOptions = [...options];
    const currentHotels = newOptions[activeOptionIndex].selected_hotels || {};
    newOptions[activeOptionIndex] = {
      ...newOptions[activeOptionIndex],
      selected_hotels: {
        ...currentHotels,
        [key]: {
          ...currentHotels[key],
          hotels: [...(currentHotels[key]?.hotels || []), newHotelEntry]
        }
      }
    };
    updateOptions(newOptions);
  }, [getRemainingNights, stopsWithDates, filters, options, activeOptionIndex, updateOptions, getBestRoomForDisplay]);

  // Remove a hotel (from active option)
  const removeHotel = useCallback((stopIndex, hotelId) => {
    const key = `stop_${stopIndex}`;
    const newOptions = [...options];
    const currentHotels = newOptions[activeOptionIndex].selected_hotels || {};
    newOptions[activeOptionIndex] = {
      ...newOptions[activeOptionIndex],
      selected_hotels: {
        ...currentHotels,
        [key]: {
          ...currentHotels[key],
          hotels: (currentHotels[key]?.hotels || []).filter(h => h.hotel_id !== hotelId)
        }
      }
    };
    updateOptions(newOptions);
  }, [options, activeOptionIndex, updateOptions]);

  // Update several fields of one selected hotel at once (in active option)
  const updateHotelFields = useCallback((stopIndex, hotelId, patch) => {
    const key = `stop_${stopIndex}`;
    const newOptions = [...options];
    const currentHotels = newOptions[activeOptionIndex].selected_hotels || {};
    newOptions[activeOptionIndex] = {
      ...newOptions[activeOptionIndex],
      selected_hotels: {
        ...currentHotels,
        [key]: {
          ...currentHotels[key],
          hotels: (currentHotels[key]?.hotels || []).map(h =>
            h.hotel_id === hotelId ? { ...h, ...patch } : h
          )
        }
      }
    };
    updateOptions(newOptions);
  }, [options, activeOptionIndex, updateOptions]);

  // Update hotel field (in active option)
  const updateHotelField = useCallback((stopIndex, hotelId, field, value) => {
    updateHotelFields(stopIndex, hotelId, { [field]: value });
  }, [updateHotelFields]);

  // Toggle upgrade selection (in active option)
  const toggleUpgrade = useCallback((stopIndex, hotelId, upgradeType, upgradeId) => {
    const key = `stop_${stopIndex}`;
    const newOptions = [...options];
    const currentHotels = newOptions[activeOptionIndex].selected_hotels || {};
    const hotels = currentHotels[key]?.hotels || [];
    
    newOptions[activeOptionIndex] = {
      ...newOptions[activeOptionIndex],
      selected_hotels: {
        ...currentHotels,
        [key]: {
          ...currentHotels[key],
          hotels: hotels.map(h => {
            if (h.hotel_id !== hotelId) return h;
            const currentUpgrades = h.upgrades?.[upgradeType] || [];
            const isSelected = currentUpgrades.includes(upgradeId);
            return {
              ...h,
              upgrades: {
                ...h.upgrades,
                [upgradeType]: isSelected 
                  ? currentUpgrades.filter(id => id !== upgradeId)
                  : [...currentUpgrades, upgradeId]
              }
            };
          })
        }
      }
    };
    updateOptions(newOptions);
  }, [options, activeOptionIndex, updateOptions]);

  // Expand/Collapse all
  const expandAll = useCallback(() => {
    setExpandedStops(stopsWithDates.map((_, i) => `stop_${i}`));
  }, [stopsWithDates]);

  const collapseAll = useCallback(() => {
    setExpandedStops([]);
  }, []);

  // Check if stop is complete
  const isStopComplete = useCallback((stopIndex) => {
    const stop = stopsWithDates[stopIndex];
    return getUsedNights(stopIndex) >= (stop?.nights || 1);
  }, [stopsWithDates, getUsedNights]);

  // Render star rating
  const renderStars = (rating) => {
    return Array.from({ length: rating || 0 }, (_, i) => (
      <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
    ));
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <div className="space-y-4 p-4">
      <div className="flex gap-3">
        <Skeleton className="h-9 w-32" />
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-9 w-32" />
        <Skeleton className="h-9 w-32" />
      </div>
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-28 w-full" />
    </div>
  );

  // Render filters section
  const renderFilters = (stop, stopIndex) => {
    const hotels = getHotelsForStop(stop);
    const stopFilters = filters[stopIndex] || { starRating: 'all', categoryBand: 'all', roomType: 'all', mealPlan: 'all' };
    
    const availableStarRatings = getAvailableStarRatings(hotels);
    const availableCategoryBands = getAvailableCategoryBands(hotels, stopFilters.starRating);
    const availableMealPlans = getAvailableMealPlans(hotels, stopFilters.starRating, stopFilters.categoryBand, stopFilters.roomType);
    const filteredHotels = getFilteredHotels(hotels, stopIndex);
    
    const hasActiveFilters = Object.values(stopFilters).some(v => v && v !== 'all');
    const hasActiveSearch = !!(hotelSearch[stopIndex] && hotelSearch[stopIndex].trim());
    const currentSort = sortBy[stopIndex] || 'price_asc';

    return (
      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3" data-testid={`filters-${stopIndex}`}>
        {/* Hotel Name Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search hotels by name..."
            value={hotelSearch[stopIndex] || ''}
            onChange={(e) => setHotelSearch(prev => ({ ...prev, [stopIndex]: e.target.value }))}
            className="pl-9 pr-9 h-10 text-sm"
            data-testid={`hotel-search-${stopIndex}`}
          />
          {hasActiveSearch && (
            <button
              onClick={() => setHotelSearch(prev => ({ ...prev, [stopIndex]: '' }))}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Alternative Rooms Indicator */}
        {exploreAlternativeRooms && (
          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200 text-xs text-blue-700">
            <HotelIcon className="w-4 h-4" />
            <span>Showing rooms that can accommodate {totalPaxNeedingBeds} passengers (villas, suites included)</span>
          </div>
        )}
        
        {/* Filters Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-600" />
            <span className="font-medium text-slate-700 text-sm">Filters</span>
          </div>
          {(hasActiveFilters || hasActiveSearch) && (
            <Button variant="ghost" size="sm" onClick={() => clearFilters(stopIndex)} className="h-7 text-xs">
              <X className="w-3 h-3 mr-1" /> Clear All
            </Button>
          )}
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {/* Star Rating */}
          <div>
            <Label className="text-xs text-slate-500">Star Rating</Label>
            <Select value={stopFilters.starRating || 'all'} onValueChange={(v) => updateFilter(stopIndex, 'starRating', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ratings</SelectItem>
                {availableStarRatings.map(r => (
                  <SelectItem key={r} value={String(r)}>{r} Star</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Category Band (with price range) */}
          <div>
            <Label className="text-xs text-slate-500">Category Band</Label>
            <Select value={stopFilters.categoryBand || 'all'} onValueChange={(v) => updateFilter(stopIndex, 'categoryBand', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {availableCategoryBands.map(band => (
                  <SelectItem key={band.band_id} value={band.category_name}>
                    {band.category_name} (₹{(band.min_rate/1000).toFixed(1)}K-{(band.max_rate/1000).toFixed(1)}K)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Room Type - Now populated from rooms array */}
          <div>
            <Label className="text-xs text-slate-500">Room Type</Label>
            <Select value={stopFilters.roomType || 'all'} onValueChange={(v) => updateFilter(stopIndex, 'roomType', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {getAvailableRoomTypes(hotels, stopFilters.starRating, stopFilters.categoryBand).map((rt, i) => (
                  <SelectItem key={i} value={rt}>{rt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Meal Plan */}
          <div>
            <Label className="text-xs text-slate-500">Meal Plan</Label>
            <Select value={stopFilters.mealPlan || 'all'} onValueChange={(v) => updateFilter(stopIndex, 'mealPlan', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                {availableMealPlans.map(mp => (
                  <SelectItem key={mp.value} value={mp.value}>{mp.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Sort and count row */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-3 h-3 text-slate-500" />
            <Select value={currentSort} onValueChange={(v) => setSortBy(prev => ({ ...prev, [stopIndex]: v }))}>
              <SelectTrigger className="h-7 w-[160px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-600">
              Showing <span className="font-semibold">{filteredHotels.length}</span> of {hotels.length} hotels
            </span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                checked={showAllHotels[stopIndex] || false}
                onCheckedChange={(c) => setShowAllHotels(prev => ({ ...prev, [stopIndex]: c }))}
                className="h-3.5 w-3.5"
              />
              <span className="text-xs text-slate-500">Show all</span>
            </label>
          </div>
        </div>
      </div>
    );
  };

  // Render hotel card
  const renderHotelCard = (hotel, stop, stopIndex, isSelected, selectedData) => {
    const remainingNights = getRemainingNights(stopIndex);
    const stopFilters = filters[stopIndex] || {};
    
    // For display: Get the best room based on filters
    const displayRoom = getBestRoomForDisplay(hotel, stopFilters);
    if (!displayRoom && !isSelected) return null; // No matching rooms
    
    // Get current data - either from selection or from display room
    const nights = selectedData?.nights || 0;
    const mealPlan = selectedData?.meal_plan || (stopFilters.mealPlan !== 'all' ? stopFilters.mealPlan : 'CP');
    
    // Get room info based on whether selected or just displaying
    const room = isSelected && selectedData ? selectedData : displayRoom;
    const roomName = room?.room_name || displayRoom?.room_name || 'Standard Room';
    const roomCategory = room?.room_category || displayRoom?.room_category;
    const gstSlab = parseInt(room?.room_gst_slab, 10) || 12;
    const extraBedGstSlab = parseInt(room?.extra_bed_gst_slab || displayRoom?.extra_bed_gst_slab, 10) || gstSlab;
    const maxOccupancy = room?.max_occupancy || 3;
    
    // Extra rate info for person-wise breakdown
    const rateExtraAdult = room?.rate_extra_adult || displayRoom?.rate_extra_adult || 0;
    const rateChildBed = room?.rate_child_bed || displayRoom?.rate_child_bed || 0;
    const rateChildNoBed = room?.rate_child_no_bed || displayRoom?.rate_child_no_bed || 0;
    
    // Check for missing child rates (warning indicators)
    const missingChildBedRate = !rateChildBed || rateChildBed === 0;
    const missingChildNoBedRate = !rateChildNoBed || rateChildNoBed === 0;
    const hasMissingRates = missingChildBedRate || missingChildNoBedRate;
    
    // Get rate from the rates object for the current meal plan
    const rates = isSelected ? (selectedData?.rates || {}) : (displayRoom?.rates || {});
    const currentMealPlanRates = rates[mealPlan] || rates['CP'] || rates['MAP'] || Object.values(rates)[0] || {};
    const baseRate = currentMealPlanRates.calculated || 0;
    
    // Calculate subtotal including child rates
    const roomCost = baseRate * nights;
    const roomGstAmount = roomCost * (gstSlab / 100);
    
    // Child costs (per night, with their own GST)
    const childrenWithBed = formData?.no_children_with_bed || 0;
    const childrenNoBed = formData?.no_children_no_bed || 0;
    const childBedCost = rateChildBed * childrenWithBed * nights;
    const childNoBedCost = rateChildNoBed * childrenNoBed * nights;
    const childGstAmount = (childBedCost + childNoBedCost) * (extraBedGstSlab / 100);
    
    const gstAmount = roomGstAmount + childGstAmount;
    const subtotal = roomCost + childBedCost + childNoBedCost + gstAmount;
    
    // Available nights for dropdown
    const availableNights = nights + remainingNights;
    const nightsOptions = Array.from({ length: availableNights }, (_, i) => i + 1);
    
    // Weekend/weekday info
    const weekendNights = hotel.weekend_nights || 0;
    const weekdayNights = hotel.weekday_nights || (hotel.total_nights || 0) - weekendNights;
    
    // Available meal plans for this room
    const availableMealPlans = isSelected 
      ? (selectedData?.available_meal_plans || Object.keys(rates))
      : Object.keys(displayRoom?.rates || {});

    // Handle meal plan change — writes to the ACTIVE OPTION's selection.
    // (Until 2026-09-27 this wrote to the legacy `hotelSelections` state that
    // nothing renders any more, so the dropdown appeared frozen.)
    const handleMealPlanChange = (newMealPlan) => {
      const hotelRates = selectedData?.rates || rates || {};
      const newRate = hotelRates[newMealPlan]?.calculated || 0;
      updateHotelFields(stopIndex, hotel.hotel_id, {
        meal_plan: newMealPlan,
        lowest_rate: newRate,
      });
    };

    return (
      <Card 
        key={hotel.hotel_id}
        className={`transition-all ${isSelected ? 'border-2 border-blue-500 bg-blue-50/30' : 'border border-slate-200 hover:border-slate-300'}`}
        data-testid={`hotel-card-${hotel.hotel_id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Radio button */}
            <button
              onClick={() => selectHotel(stopIndex, hotel, displayRoom)}
              data-testid={`select-hotel-${hotel.hotel_id}`}
              className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300 hover:border-blue-400'
              }`}
            >
              {isSelected && <Check className="w-3 h-3 text-white" />}
            </button>
            
            {/* Hotel info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-slate-900">{hotel.hotel_name}</h4>
                <div className="flex">{renderStars(hotel.star_rating)}</div>
                {isSelected && <Badge className="bg-green-100 text-green-700 text-xs">SELECTED</Badge>}
                {/* Warning badge for missing child rates */}
                {hasMissingRates && (
                  <Badge 
                    variant="outline" 
                    className="text-xs bg-amber-50 text-amber-700 border-amber-300 cursor-help"
                    title={`Missing rates: ${[missingChildBedRate && 'Child w/Bed', missingChildNoBedRate && 'Child w/o Bed'].filter(Boolean).join(', ')}`}
                  >
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Child rates missing
                  </Badge>
                )}
              </div>
              
              {/* Room info with category */}
              <div className="mt-1 text-sm text-slate-600">
                <span className="font-medium text-green-700">{roomName}</span>
                {roomCategory && (
                  <Badge variant="outline" className="ml-2 text-xs bg-purple-50 text-purple-700 border-purple-200">
                    {roomCategory}
                  </Badge>
                )}
                {maxOccupancy > 3 && (
                  <Badge variant="outline" className="ml-1 text-xs bg-blue-50 text-blue-700 border-blue-200">
                    Max {maxOccupancy} pax
                  </Badge>
                )}
              </div>
              
              {/* Rate with meal plan info */}
              <div className="mt-1 text-sm">
                <span className="font-semibold text-slate-900">₹{baseRate.toLocaleString()}/night</span>
                <span className="text-slate-500 text-xs ml-1">({MEAL_PLANS.find(m => m.value === mealPlan)?.label || mealPlan})</span>
                <span className="text-slate-500 text-xs ml-1">+ {gstSlab}% GST</span>
                {/* Rate breakdown toggle */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRateBreakdown(prev => ({ ...prev, [hotel.hotel_id]: !prev[hotel.hotel_id] }));
                  }}
                  className="ml-2 text-blue-600 hover:text-blue-700 text-xs underline"
                >
                  {showRateBreakdown[hotel.hotel_id] ? 'Hide rates' : 'View all rates'}
                </button>
              </div>
              
              {/* Expandable Rate Breakdown Section */}
              {showRateBreakdown[hotel.hotel_id] && (
                <div className="mt-2 p-2 bg-slate-50 rounded-lg border border-slate-200 text-xs">
                  <div className="font-medium text-slate-700 mb-1.5 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    Rate Breakdown (Per Night)
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Double Occupancy:</span>
                      <span className="font-medium">₹{baseRate.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Room GST:</span>
                      <span className="font-medium">{gstSlab}%</span>
                    </div>
                    <div className={`flex justify-between ${rateExtraAdult === 0 ? 'text-amber-600' : ''}`}>
                      <span className="text-slate-600">Extra Adult Bed:</span>
                      <span className="font-medium">{rateExtraAdult > 0 ? `₹${rateExtraAdult.toLocaleString()}` : 'Not set'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Bed GST:</span>
                      <span className="font-medium">{extraBedGstSlab}%</span>
                    </div>
                    <div className={`flex justify-between ${missingChildBedRate ? 'text-amber-600' : ''}`}>
                      <span className="text-slate-600">Child w/ Bed:</span>
                      <span className="font-medium">{rateChildBed > 0 ? `₹${rateChildBed.toLocaleString()}` : 
                        <span className="flex items-center gap-0.5">
                          <AlertCircle className="w-3 h-3" />
                          Not set
                        </span>
                      }</span>
                    </div>
                    <div className={`flex justify-between ${missingChildNoBedRate ? 'text-amber-600' : ''}`}>
                      <span className="text-slate-600">Child w/o Bed:</span>
                      <span className="font-medium">{rateChildNoBed > 0 ? `₹${rateChildNoBed.toLocaleString()}` : 
                        <span className="flex items-center gap-0.5">
                          <AlertCircle className="w-3 h-3" />
                          Not set
                        </span>
                      }</span>
                    </div>
                  </div>
                  {hasMissingRates && (
                    <div className="mt-2 pt-2 border-t border-slate-200 text-amber-700 flex items-start gap-1">
                      <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>Missing child rates will use Extra Adult rate as fallback during quote calculation</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Rate info - weekday/weekend */}
              {(weekendNights > 0 || hotel.rate_breakdown?.length > 0) && (
                <div className="mt-1 text-xs text-slate-500">
                  {stop.check_in_display} - {stop.check_out_display} 
                  {weekendNights > 0 && ` (${weekdayNights} WD, ${weekendNights} WE)`}
                </div>
              )}
              
              {/* Selection controls */}
              {isSelected && (
                <>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500">Meal Plan:</Label>
                      <Select value={mealPlan} onValueChange={handleMealPlanChange}>
                        <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`meal-plan-select-${hotel.hotel_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableMealPlans.map(mp => {
                            const info = MEAL_PLANS.find(m => m.value === mp);
                            const mpRate = rates[mp]?.calculated || 0;
                            return (
                              <SelectItem key={mp} value={mp}>
                                {info?.label || mp} (₹{mpRate.toLocaleString()})
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500">Nights:</Label>
                      <Select value={String(nights)} onValueChange={(v) => updateHotelField(stopIndex, hotel.hotel_id, 'nights', parseInt(v))}>
                        <SelectTrigger className="h-8 w-[70px] text-xs" data-testid={`nights-select-${hotel.hotel_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {nightsOptions.map(n => (
                            <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => removeHotel(stopIndex, hotel.hotel_id)}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  </div>
                  
                  {/* Subtotal */}
                  {nights > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between text-slate-600">
                          <span>Room: {nights}N × ₹{baseRate.toLocaleString()}</span>
                          <span>₹{roomCost.toLocaleString()}</span>
                        </div>
                        {childrenWithBed > 0 && rateChildBed > 0 && (
                          <div className="flex justify-between text-slate-600">
                            <span>Child w/Bed: {childrenWithBed} × {nights}N × ₹{rateChildBed.toLocaleString()}</span>
                            <span>₹{childBedCost.toLocaleString()}</span>
                          </div>
                        )}
                        {childrenNoBed > 0 && rateChildNoBed > 0 && (
                          <div className="flex justify-between text-slate-600">
                            <span>Child w/o Bed: {childrenNoBed} × {nights}N × ₹{rateChildNoBed.toLocaleString()}</span>
                            <span>₹{childNoBedCost.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-slate-600">
                          <span>GST ({gstSlab}%)</span>
                          <span>₹{gstAmount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between font-semibold text-slate-900">
                          <span>Subtotal</span>
                          <span>₹{subtotal.toLocaleString()}</span>
                        </div>
                      </div>
                      
                      {/* Upgrades toggle */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 w-full justify-between text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => setShowUpgrades(prev => ({ ...prev, [hotel.hotel_id]: !prev[hotel.hotel_id] }))}
                        data-testid={`view-room-options-${hotel.hotel_id}`}
                      >
                        <span>View Room Options ({(hotel.rooms || []).length} rooms available)</span>
                        {showUpgrades[hotel.hotel_id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                      
                      {/* Room options section */}
                      {showUpgrades[hotel.hotel_id] && (
                        <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                          <h5 className="text-xs font-medium text-slate-700">All Room Options</h5>
                          {(hotel.rooms || []).map((altRoom, idx) => {
                            const altMealPlans = Object.keys(altRoom.rates || {});
                            const altRate = altRoom.rates?.[mealPlan]?.calculated || altRoom.rates?.['CP']?.calculated || 0;
                            const isCurrentRoom = altRoom.room_name === roomName;
                            
                            return (
                              <div 
                                key={idx}
                                className={`p-2 rounded border cursor-pointer transition-all ${
                                  isCurrentRoom ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-blue-300'
                                }`}
                                onClick={() => {
                                  // Switch to this room — writes to the ACTIVE
                                  // OPTION (was writing to the dead legacy
                                  // selections state, so clicks did nothing).
                                  const newMealPlan = altMealPlans.includes(mealPlan) ? mealPlan : altMealPlans[0];
                                  const newRate = altRoom.rates?.[newMealPlan]?.calculated || 0;
                                  updateHotelFields(stopIndex, hotel.hotel_id, {
                                    room_name: altRoom.room_name,
                                    room_type: altRoom.room_type,
                                    room_category: altRoom.room_category,
                                    room_gst_slab: altRoom.room_gst_slab ?? null,
                                    max_occupancy: altRoom.max_occupancy || 3,
                                    rate_extra_adult: altRoom.rate_extra_adult || 0,
                                    rate_child_bed: altRoom.rate_child_bed || 0,
                                    rates: altRoom.rates || {},
                                    available_meal_plans: altMealPlans,
                                    meal_plan: newMealPlan,
                                    lowest_rate: newRate,
                                  });
                                }}
                                data-testid={`room-option-${hotel.hotel_id}-${idx}`}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <span className="text-sm font-medium">{altRoom.room_name}</span>
                                    {altRoom.room_category && (
                                      <Badge variant="outline" className="ml-2 text-xs">
                                        {altRoom.room_category}
                                      </Badge>
                                    )}
                                    {altRoom.max_occupancy > 3 && (
                                      <Badge variant="outline" className="ml-1 text-xs bg-blue-50">
                                        Max {altRoom.max_occupancy} pax
                                      </Badge>
                                    )}
                                  </div>
                                  <span className="text-sm font-semibold">₹{altRate.toLocaleString()}/night</span>
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                  Available: {altMealPlans.map(mp => MEAL_PLANS.find(m => m.value === mp)?.label || mp).join(', ')}
                                  {altRoom.room_gst_slab && ` | GST: ${altRoom.room_gst_slab}%`}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render hotel list
  const renderHotelList = (stop, stopIndex) => {
    const hotels = getHotelsForStop(stop);
    const filteredHotels = getFilteredHotels(hotels, stopIndex);
    const showAll = showAllHotels[stopIndex] || false;
    
    // Use active option's selected_hotels
    const key = `stop_${stopIndex}`;
    const activeOption = options[activeOptionIndex];
    const selection = activeOption?.selected_hotels?.[key] || { hotels: [] };
    const selectedHotelIds = (selection.hotels || []).map(h => h.hotel_id);
    
    const hotelsToDisplay = showAll ? hotels : filteredHotels;
    
    if (hotelsToDisplay.length === 0) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">No hotels found matching your filters</p>
          <p className="text-slate-400 text-xs mt-1">Try adjusting the filters above</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4 text-purple-600 border-purple-200 hover:bg-purple-50"
            onClick={() => {
              setQuickAddHotelPlaceId(stop.place_id);
              setQuickAddHotelStopIndex(stopIndex);
              setQuickAddHotelOpen(true);
            }}
            data-testid={`quick-add-hotel-stop-${stopIndex}`}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Add New Hotel for {stop.place_name}
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-3 mt-4">
        <h4 className="font-medium text-slate-700 flex items-center gap-2 text-sm">
          <HotelIcon className="w-4 h-4" />
          Available Hotels
        </h4>
        
        {hotelsToDisplay.map(hotel => {
          const isSelected = selectedHotelIds.includes(hotel.hotel_id);
          const isFiltered = filteredHotels.some(h => h.hotel_id === hotel.hotel_id);
          const selectedData = isSelected ? selection.hotels.find(h => h.hotel_id === hotel.hotel_id) : null;
          
          // Skip non-matching hotels when not showing all
          if (!showAll && !isFiltered) return null;
          
          // Gray out non-matching when showing all
          if (showAll && !isFiltered) {
            return (
              <Card key={hotel.hotel_id} className="border border-slate-200 bg-slate-50 opacity-60">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-slate-300 bg-slate-200 mt-1" />
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-500">{hotel.hotel_name}</h4>
                        <div className="flex opacity-50">{renderStars(hotel.star_rating)}</div>
                      </div>
                      <Badge variant="secondary" className="text-xs mt-1">Doesn't match filters</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }
          
          return renderHotelCard(hotel, stop, stopIndex, isSelected, selectedData);
        })}
        
        {/* Remaining nights info */}
        {getRemainingNights(stopIndex) > 0 && selectedHotelIds.length > 0 && (
          <div className="pt-3 border-t border-slate-200 text-sm text-slate-500">
            <span className="font-medium">{getRemainingNights(stopIndex)} night{getRemainingNights(stopIndex) > 1 ? 's' : ''}</span> remaining to allocate.
            Select another hotel above for a split stay.
          </div>
        )}
      </div>
    );
  };

  // Render Markup/Discount/GST Configuration (Shared across all options)
  const renderConfig = () => {
    const updateField = (field, value) => {
      // handleInputChange expects (field, value) not an event object
      handleInputChange?.(field, value);
    };

    return (
      <>
        {/* Pricing Configuration - SHARED across all options */}
        <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Pricing Configuration (Shared)
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">These settings apply to all package options</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Markup & Discount */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-slate-700">Markup & Discount</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Markup Type</Label>
                <div className="flex gap-2">
                  <Button
                    variant={formData.markup_type === 'percent' ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1"
                    onClick={() => updateField('markup_type', 'percent')}
                  >
                    <Percent className="w-3 h-3 mr-1" /> Percentage
                  </Button>
                  <Button
                    variant={formData.markup_type === 'amount' ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1"
                    onClick={() => updateField('markup_type', 'amount')}
                  >
                    <IndianRupee className="w-3 h-3 mr-1" /> Fixed
                  </Button>
                </div>
                <Input
                  type="number"
                  placeholder={formData.markup_type === 'percent' ? 'Markup %' : 'Markup Amount'}
                  value={formData.markup_type === 'percent' ? (formData.markup_pct || '') : (formData.markup_amount || '')}
                  onChange={(e) => updateField(formData.markup_type === 'percent' ? 'markup_pct' : 'markup_amount', parseFloat(e.target.value) || 0)}
                  className="h-9"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">Discount Type</Label>
                <div className="flex gap-2">
                  <Button
                    variant={formData.discount_type === 'percent' ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1"
                    onClick={() => updateField('discount_type', 'percent')}
                  >
                    <Percent className="w-3 h-3 mr-1" /> Percentage
                  </Button>
                  <Button
                    variant={formData.discount_type === 'amount' ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1"
                    onClick={() => updateField('discount_type', 'amount')}
                  >
                    <IndianRupee className="w-3 h-3 mr-1" /> Fixed
                  </Button>
                </div>
                <Input
                  type="number"
                  placeholder={formData.discount_type === 'percent' ? 'Discount %' : 'Discount Amount'}
                  value={formData.discount_type === 'percent' ? (formData.discount_pct || '') : (formData.discount_amount || '')}
                  onChange={(e) => updateField(formData.discount_type === 'percent' ? 'discount_pct' : 'discount_amount', parseFloat(e.target.value) || 0)}
                  className="h-9"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label className="text-sm">Show Cost Breakup in Quote</Label>
              <Switch
                checked={formData.show_breakup || false}
                onCheckedChange={(c) => updateField('show_breakup', c)}
              />
            </div>
          </div>
          
          {/* GST Configuration */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium text-sm text-slate-700">GST Configuration</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">GST Rate</Label>
                <Select value={String(formData.gst_percentage || '0')} onValueChange={(v) => updateField('gst_percentage', parseInt(v))}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GST_RATES.map(r => (
                      <SelectItem key={r} value={r}>{r === '0' ? 'No GST (0%)' : `${r}%`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">Apply GST On</Label>
                <Select 
                  value={formData.gst_apply_on || 'total'} 
                  onValueChange={(v) => updateField('gst_apply_on', v)}
                  disabled={parseInt(formData.gst_percentage) === 0}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GST_APPLY_ON.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData.gst_included || false}
                onCheckedChange={(c) => updateField('gst_included', c)}
                disabled={parseInt(formData.gst_percentage) === 0}
              />
              <Label className="text-sm">
                {formData.gst_included ? 'GST Included in Quote' : 'Show GST Separately'}
              </Label>
            </div>
            
            {formData.gst_percentage > 0 && (
              <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                {formData.gst_apply_on === 'markup'
                  ? `GST @ ${formData.gst_percentage}% will be applied on markup amount only`
                  : `GST @ ${formData.gst_percentage}% will be applied on total package amount`}
                {formData.gst_included ? ' (included in price)' : ' (shown separately)'}
              </p>
            )}
          </div>
          
          {/* Quotation Format */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium text-sm text-slate-700">Quotation Format</h4>
            <RadioGroup
              value={formData.quotation_format || 'total'}
              onValueChange={(v) => updateField('quotation_format', v)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              <div className={`flex items-start space-x-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.quotation_format === 'total' || !formData.quotation_format ? 'border-navy bg-white' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                <RadioGroupItem value="total" id="cp-format-total" className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <Label htmlFor="cp-format-total" className="cursor-pointer font-medium text-sm">Total Package</Label>
                  <p className="text-xs text-gray-600 mt-0.5">Single total price for the entire group</p>
                </div>
              </div>
              <div className={`flex items-start space-x-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.quotation_format === 'person_wise' ? 'border-navy bg-white' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                <RadioGroupItem value="person_wise" id="cp-format-person" className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <Label htmlFor="cp-format-person" className="cursor-pointer font-medium text-sm">Person-wise</Label>
                  <p className="text-xs text-gray-600 mt-0.5">Adults pay hotel + transport, Children pay bed only</p>
                </div>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>
      </>
    );
  };

  // Render Quote Content Boxes (Inclusions, Exclusions, Terms from Agency Masters)
  const renderQuoteContentBoxes = () => {
    // Group masters by type
    const inclusions = agencyMasters.filter(m => m.master_type === 'INCLUSION' && m.is_active);
    const exclusions = agencyMasters.filter(m => m.master_type === 'EXCLUSION' && m.is_active);
    const terms = agencyMasters.filter(m => m.master_type === 'TERMS' && m.is_active);
    const notes = agencyMasters.filter(m => m.master_type === 'NOTE' && m.is_active);
    const cancellations = agencyMasters.filter(m => m.master_type === 'CANCELLATION' && m.is_active);
    
    // Content sections to display
    const sections = [
      { key: 'inclusions', title: 'Inclusions', data: inclusions, icon: Check, color: 'text-green-600' },
      { key: 'exclusions', title: 'Exclusions', data: exclusions, icon: X, color: 'text-red-600' },
      { key: 'terms', title: 'Terms & Conditions', data: terms, icon: AlertCircle, color: 'text-orange-600' },
      { key: 'notes', title: 'Important Notes', data: notes, icon: AlertCircle, color: 'text-blue-600' },
      { key: 'cancellations', title: 'Cancellation Policy', data: cancellations, icon: AlertCircle, color: 'text-purple-600' },
    ].filter(s => s.data.length > 0);
    
    if (sections.length === 0) return null;

    return (
      <Card className="mt-4 border-navy/20">
        <CardHeader className="pb-2 bg-navy/5 border-b border-navy/15">
          <CardTitle className="text-sm flex items-center gap-2 text-navy">
            <Settings className="w-4 h-4" />
            Quote Details
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <Accordion type="multiple" className="w-full">
            {sections.map((section) => (
              <AccordionItem key={section.key} value={section.key} className="border-b border-gray-100">
                <AccordionTrigger className="py-2 text-sm hover:no-underline">
                  <span className={`flex items-center gap-2 ${section.color}`}>
                    <section.icon className="w-4 h-4" />
                    {section.title}
                    <Badge variant="outline" className="ml-2 text-xs">{section.data.length}</Badge>
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2 text-sm text-gray-700 pl-4">
                    {section.data.map((item, idx) => {
                      // Split content by newlines and filter out empty lines
                      const lines = (item.content || '').split('\n').filter(line => line.trim());
                      return (
                        <li key={item.master_id || idx} className="space-y-1">
                          {lines.map((line, lineIdx) => (
                            <div key={lineIdx} className="flex items-start gap-2">
                              <span className="text-gray-400 mt-0.5">•</span>
                              <span className="flex-1">{line.trim()}</span>
                            </div>
                          ))}
                        </li>
                      );
                    })}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    );
  };

  // Render Miscellaneous Costs Section
  const renderMiscCosts = () => {
    const miscCosts = formData.misc_costs || [];
    
    const addMiscCost = () => {
      const newCost = {
        id: Date.now(),
        remarks: '',
        cost_type: 'total',
        amount: 0,
        gst_percentage: 0,
        is_optional: false
      };
      handleInputChange?.('misc_costs', [...miscCosts, newCost]);
    };
    
    const updateMiscCost = (id, field, value) => {
      const updated = miscCosts.map(cost => 
        cost.id === id ? { ...cost, [field]: value } : cost
      );
      handleInputChange?.('misc_costs', updated);
    };
    
    const removeMiscCost = (id) => {
      const updated = miscCosts.filter(cost => cost.id !== id);
      handleInputChange?.('misc_costs', updated);
    };
    
    return (
      <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Miscellaneous Costs
            </span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-misc-table-cp"
                  checked={formData.show_misc_table !== false}
                  onCheckedChange={(checked) => handleInputChange?.('show_misc_table', checked)}
                />
                <Label htmlFor="show-misc-table-cp" className="text-xs cursor-pointer">Show as Table</Label>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addMiscCost}
                className="h-8 text-xs"
                data-testid="add-misc-cost-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Cost
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {miscCosts.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-4">No miscellaneous costs added. Click "Add Cost" to add entry fees, activities, etc.</p>
          ) : (
            <div className="space-y-3">
              {miscCosts.map((cost, miscIdx) => (
                <div key={cost.id} className={`p-3 rounded-lg border ${cost.is_optional ? 'border-amber-300 bg-amber-50' : 'bg-slate-50 border-slate-200'}`} data-testid={`misc-row-${miscIdx}`}>
                  <div className="flex flex-col lg:flex-row lg:items-end gap-2">
                    {/* Description */}
                    <div className="flex-1 min-w-0">
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Description</Label>
                      <Input
                        placeholder="e.g., Entry tickets, Guide charges"
                        value={cost.remarks}
                        onChange={(e) => updateMiscCost(cost.id, 'remarks', e.target.value)}
                        className="h-9 text-sm"
                        data-testid={`misc-remarks-${miscIdx}`}
                      />
                    </div>
                    
                    {/* Type */}
                    <div className="w-full sm:w-28 lg:w-28">
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Type</Label>
                      <Select
                        value={cost.cost_type}
                        onValueChange={(v) => updateMiscCost(cost.id, 'cost_type', v)}
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COST_TYPES.map(ct => (
                            <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Amount */}
                    <div className="w-full sm:w-24 lg:w-24">
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">Amount</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={cost.amount}
                        onChange={(e) => updateMiscCost(cost.id, 'amount', parseFloat(e.target.value) || 0)}
                        className="h-9 text-sm"
                        data-testid={`misc-amount-${miscIdx}`}
                      />
                    </div>
                    
                    {/* GST */}
                    <div className="w-full sm:w-20 lg:w-20">
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 block">GST</Label>
                      <Select
                        value={String(cost.gst_percentage || 0)}
                        onValueChange={(v) => updateMiscCost(cost.id, 'gst_percentage', parseFloat(v))}
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue placeholder="GST" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">No GST</SelectItem>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="12">12%</SelectItem>
                          <SelectItem value="18">18%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Optional */}
                    <div className="w-full sm:w-20 lg:w-20 flex items-center gap-2">
                      <Checkbox
                        id={`optional-${cost.id}`}
                        checked={cost.is_optional}
                        onCheckedChange={(checked) => updateMiscCost(cost.id, 'is_optional', checked)}
                        data-testid={`misc-optional-${miscIdx}`}
                      />
                      <Label htmlFor={`optional-${cost.id}`} className="text-xs cursor-pointer">Optional</Label>
                    </div>
                    
                    {/* Delete */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMiscCost(cost.id)}
                      className="h-9 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Read-only summary of a non-active option. Renders THAT option's own
  // hotels; the accordion below only ever edits the active option, which is
  // why every card used to show identical selections.
  const renderOptionSummary = (option, optionIndex) => {
    const rows = stopsWithDates.map((stop, stopIndex) => ({
      stop,
      hotels: option.selected_hotels?.[`stop_${stopIndex}`]?.hotels || [],
    }));
    const hasAny = rows.some((r) => r.hotels.length > 0);

    return (
      <div className="space-y-2" data-testid={`option-summary-${optionIndex}`}>
        {!hasAny ? (
          <p className="text-sm text-slate-500 italic">No hotels selected yet for this option.</p>
        ) : (
          rows.map(({ stop, hotels }, stopIndex) => (
            <div
              key={`summary_${optionIndex}_${stopIndex}`}
              className="flex items-start gap-3 text-sm p-2 rounded border border-slate-200 bg-slate-50"
            >
              <MapPin className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
              <div className="w-32 shrink-0 font-medium text-slate-700">{stop.place_name}</div>
              <div className="flex-1 space-y-0.5">
                {hotels.length === 0 ? (
                  <span className="text-slate-400 italic">Not selected</span>
                ) : (
                  hotels.map((h) => (
                    <div key={h.hotel_id} className="text-slate-600">
                      {h.hotel_name}
                      <span className="text-slate-400">
                        {' '}· {h.room_name || h.room_type || '—'} · {h.meal_plan} · {h.nights}N
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setActiveOptionIndex(optionIndex)}
          data-testid={`edit-option-${optionIndex}`}
        >
          Edit this option
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="custom-package-hotel-selector">
      {/* Guard for missing customPackageData */}
      {!customPackageData && (
        <div className="text-center py-8 text-gray-500">
          <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p>Loading package configuration...</p>
        </div>
      )}
      
      {customPackageData && (
        <>
          {/* Options Header */}
          <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <Layers className="w-5 h-5 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Package Options</h3>
                <p className="text-sm text-slate-500">Create multiple hotel options for comparison. Vehicle, transfers &amp; pricing are set in the next step.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {options.length} / {MAX_OPTIONS} options
              </Badge>
              {options.length < MAX_OPTIONS && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addOption}
                  className="bg-white"
                  data-testid="add-option-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Option
                </Button>
              )}
            </div>
          </div>

          {/* Option Cards */}
          <div className="space-y-4">
            {options.map((option, optionIndex) => (
              <Card 
                key={option.id} 
                className={`border-2 transition-all ${
                  activeOptionIndex === optionIndex 
                    ? 'border-blue-500 shadow-md' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`option-card-${optionIndex}`}
              >
                {/* Option Header */}
                <div 
                  className={`flex items-center justify-between p-4 cursor-pointer ${
                    option.collapsed ? 'border-b-0' : 'border-b'
                  }`}
                  onClick={() => {
                    setActiveOptionIndex(optionIndex);
                    if (option.collapsed) toggleOptionCollapse(optionIndex);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleOptionCollapse(optionIndex); }}
                      className="p-1 hover:bg-slate-100 rounded"
                    >
                      {option.collapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                    </button>
                    <div className="flex items-center gap-2">
                      <Input
                        value={option.option_name}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateOptionField(optionIndex, 'option_name', e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="font-semibold text-lg border-none shadow-none p-0 h-auto w-40 focus-visible:ring-0"
                        placeholder="Option name"
                        data-testid={`option-name-${optionIndex}`}
                      />
                      {activeOptionIndex === optionIndex && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Active</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Option summary badges */}
                    {Object.keys(option.selected_hotels || {}).length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        <HotelIcon className="w-3 h-3 mr-1" />
                        {Object.keys(option.selected_hotels).length} hotels
                      </Badge>
                    )}
                    {/* Action buttons */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); copyOption(optionIndex); }}
                      disabled={options.length >= MAX_OPTIONS}
                      title="Copy this option"
                      data-testid={`copy-option-${optionIndex}`}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    {options.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); removeOption(optionIndex); }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        title="Remove this option"
                        data-testid={`remove-option-${optionIndex}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Option Content (collapsible) */}
                {!option.collapsed && (
                  <CardContent className="p-4 space-y-6">
                    {activeOptionIndex !== optionIndex ? renderOptionSummary(option, optionIndex) : (
                      <>
                    {/* Hotels Section Header */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-md font-semibold text-slate-900 flex items-center gap-2">
                          <HotelIcon className="w-4 h-4 text-blue-600" />
                          Select Hotels
                        </h4>
                        <p className="text-sm text-slate-500">Choose hotels for each stop in your tour</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={expandAll}>
                          <ChevronsUpDown className="w-4 h-4 mr-1" /> Expand All
                        </Button>
                        <Button variant="outline" size="sm" onClick={collapseAll}>
                          Collapse All
                        </Button>
                      </div>
                    </div>

          {/* No stops message */}
          {stopsWithDates.length === 0 && (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No stops defined</p>
          <p className="text-sm text-slate-400 mt-1">Please add places in the previous step</p>
        </div>
      )}

      {/* Accordion for stops */}
      {stopsWithDates.length > 0 && (
        <Accordion type="multiple" value={expandedStops} onValueChange={setExpandedStops} className="space-y-3">
          {stopsWithDates.map((stop, stopIndex) => {
            const key = `stop_${stopIndex}`;
            const cacheKey = `${stop.place_id}_${stop.check_in}_${stop.check_out}`;
            const usedNights = getUsedNights(stopIndex);
            const isComplete = isStopComplete(stopIndex);
            const isLoading = initialLoading || !hotelsCache[cacheKey];

            return (
              <AccordionItem
                key={key}
                value={key}
                className="border rounded-lg overflow-hidden bg-white shadow-sm"
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">{stop.place_name}</span>
                          <span className="text-sm text-slate-500">({stop.nights} night{stop.nights > 1 ? 's' : ''})</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                          <Calendar className="w-3 h-3" />
                          <span>{stop.check_in_display} → {stop.check_out_display}</span>
                          {stop.weekend_nights > 0 && (
                            <Badge variant="secondary" className="text-xs ml-1">
                              {stop.weekday_nights}WD + {stop.weekend_nights}WE
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge className={isComplete ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                      {isComplete && <Check className="w-3 h-3 mr-1" />}
                      {usedNights}/{stop.nights} allocated
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  {isLoading ? renderSkeleton() : (
                    <>
                      {renderFilters(stop, stopIndex)}
                      {renderHotelList(stop, stopIndex)}
                    </>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

                      </>
                    )}
                  </CardContent>
                )}
              </Card>
            ))}
          </div>

      {/* Pricing Configuration (SHARED - includes Transfer/Vehicle section) */}
      {stopsWithDates.length > 0 && handleInputChange && renderConfig()}

      {/* Step 5B (2026-04-30): Inline Quote Preview removed per
          SPEC_CUSTOM_PACKAGE_PREVIEW_UNIFICATION.md Step A.
          Canonical preview now lives in PreferencesStep.js Step 3 Custom branch
          (via QuotationDisplay/* shared components). */}

      {/* #289720: Miscellaneous Costs moved to step 3 (PreferencesStep, Custom branch).
          renderMiscCosts() is intentionally no longer rendered here. */}
      
      {/* Quote Content Boxes removed - now only in TermsSection */}
        </>
      )}
      
      {/* Quick Add Hotel Modal */}
      <QuickAddHotelModal
        open={quickAddHotelOpen}
        onOpenChange={setQuickAddHotelOpen}
        prefillPlaceId={quickAddHotelPlaceId}
        places={places}
        onSuccess={(newHotel) => {
          // Refresh hotels cache for this place
          if (quickAddHotelStopIndex !== null) {
            const stop = stops[quickAddHotelStopIndex];
            if (stop) {
              const cacheKey = `${stop.place_id}_${stop.check_in}_${stop.check_out}`;
              // Clear cache to force refetch
              setHotelsCache(prev => {
                const updated = { ...prev };
                delete updated[cacheKey];
                return updated;
              });
              // Trigger refetch
              setFetchingHotels(true);
            }
          }
        }}
      />
    </div>
  );
};

export default CustomPackageHotelSelector;
